import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { THEME_KEY, readSetting, writeSetting } from './settings';

export type Theme = 'dark' | 'light';

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeState | null>(null);

/**
 * 主题（需求：深色默认，浅色可切，偏好持久化）
 *
 * 独立于 AppShell 的原因：lightweight-charts 不读 CSS 变量，
 * 图表必须知道具体色值，而它位于 AppShell 的子树深处。
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readSetting<Theme>(THEME_KEY, 'dark'));

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    writeSetting(THEME_KEY, theme);
  }, [theme]);

  const value = useMemo<ThemeState>(
    () => ({
      theme,
      setTheme: setThemeState,
      toggle: () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    }),
    [theme],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme 必须在 ThemeProvider 内使用');
  return ctx;
}